import React from 'react';
import { BarChart3 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { KeyMetrics } from '../../components/whatsapp/analytics/KayMetrics';
import { DailyBreakdown } from '../../components/whatsapp/analytics/DailyBreakdown';
import { AnalyticsHeader } from '../../components/whatsapp/analytics/AnalyticsHeader';
import { useAnalyticsData } from './hooks/useAnalyticsData';
import { Button } from '../../components/tailwind/button';
import WhatsAppConnectionNotice from "../../components/whatsapp/WhatsAppConnectionNotice";
import { usePageTitle } from "../../hooks/usePageTitle";
import { whatsappPageShell, waShell } from "./utils/whatsappPageShell";
import { WhatsAppPageIntro } from "./components/WhatsAppPageIntro";
import { AnalyticsSkeleton } from "./components/skeletons/WhatsAppPageSkeletons";

export default function Analytics() {
  usePageTitle('Analytics & Usage');
  const navigate = useNavigate();
  const {
    dateRange,
    dailyData,
    emailReport,
    loading,
    error,
    wabaAccounts,
    setRange,
    setEmailOpen,
    setEmail,
    setFrequency,
  } = useAnalyticsData();

  const primaryWabaId = Array.isArray(wabaAccounts) && wabaAccounts.length > 0 ? (wabaAccounts[0]?.wabaId || '') : '';

  const intro = (
    <WhatsAppPageIntro
      description="Track message delivery and engagement across your WhatsApp account."
      actions={
        dailyData.length > 0 ? (
          <AnalyticsHeader
            dateRange={dateRange}
            setDateRange={setRange}
            emailReportOpen={emailReport.open}
            setEmailReportOpen={setEmailOpen}
            reportEmail={emailReport.email}
            setReportEmail={setEmail}
            reportFrequency={emailReport.frequency}
            setReportFrequency={setFrequency}
          />
        ) : null
      }
    />
  );

  if (loading && dailyData.length === 0) {
    return <AnalyticsSkeleton />;
  }

  if (error && dailyData.length === 0) {
    return (
      <div className={waShell(whatsappPageShell.fluid, "flex items-center justify-center")}>
        <div className="text-center">
          <p className="text-red-500 font-semibold mb-2">Failed to load analytics</p>
          <p className="text-gray-500 dark:text-gray-400 text-sm">{error}</p>
        </div>
      </div>
    );
  }

  if (!loading && !error && dailyData.length === 0) {
    return (
      <div className={waShell(whatsappPageShell.fluid, "animate-fade-in !space-y-4")}>
        {intro}
        <div className="flex flex-col items-center justify-center py-24 bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-800 border-dashed text-center px-4 animate-fade-in-up">
          <div className="w-16 h-16 bg-gray-50 dark:bg-slate-800 rounded-full flex items-center justify-center mb-4">
            <BarChart3 className="w-8 h-8 text-gray-400" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Awaiting Data</h2>
          <p className="text-gray-500 dark:text-gray-400 max-w-md mb-6">Your analytics will appear here once messages are sent.</p>
          <Button variant="outline" className="text-gray-500 text-xs" onClick={() => navigate('/whatsapp/send-message')}>
            Start Sending Messages
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={waShell(whatsappPageShell.fluid, "animate-fade-in !space-y-4 sm:!space-y-5")}>
      {intro}

      {!primaryWabaId && <WhatsAppConnectionNotice />}
      <div className={`space-y-5 ${!primaryWabaId ? 'pointer-events-none opacity-50' : ''} animate-fade-in-up`}>
        <KeyMetrics dailyData={dailyData} />
        <DailyBreakdown data={dailyData} />
      </div>
    </div>
  );
}
